import { FC, useCallback, useEffect, useState } from 'react';
import {
  App,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Typography
} from 'antd';
import { fetchNumberCounter, setNumberCounter } from 'services/api/invoicing';
import { NumberCounter } from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Super-admin invoice-number counter: view last/next and set the next number
 * (forward-only — the BE rejects a value below the current next or ≤ one
 * already used). The counter is per prefix; empty prefix = the env default.
 */
const NumberingModal: FC<IProps> = ({ open, onClose }) => {
  const { message } = App.useApp();
  const [counter, setCounter] = useState<NumberCounter | null>(null);
  const [prefix, setPrefix] = useState('');
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (p: string) => {
    try {
      setLoading(true);
      const res = await fetchNumberCounter(p || undefined);
      setCounter(res);
      setNextNumber(res.nextValue);
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPrefix('');
    load('');
  }, [open, load]);

  const onSave = async () => {
    if (nextNumber == null) {
      return;
    }

    try {
      setSaving(true);
      const res = await setNumberCounter(nextNumber, prefix || undefined);
      setCounter(res);
      setNextNumber(res.nextValue);
      message.success(`Next invoice number set to ${res.nextValue}`);
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onSave}
      okText="Set next number"
      okButtonProps={{ loading: saving, disabled: loading }}
      title="Invoice numbering"
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Form layout="vertical">
          <Form.Item
            label="Prefix (optional — empty = default)"
            style={{ marginBottom: 12 }}
          >
            <Input.Search
              value={prefix}
              placeholder="e.g. 5026F546"
              enterButton="Load"
              onChange={e => setPrefix(e.target.value)}
              onSearch={value => load(value)}
            />
          </Form.Item>
        </Form>

        {loading ? (
          <Spin />
        ) : counter ? (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Scope">{counter.scope}</Descriptions.Item>
            <Descriptions.Item label="Last issued">
              {counter.lastValue}
            </Descriptions.Item>
            <Descriptions.Item label="Next number">
              {counter.nextValue}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        <Form layout="vertical">
          <Form.Item
            label="Set next number to"
            help="Forward-only: must be ≥ the current next and above any number already used."
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              value={nextNumber ?? undefined}
              onChange={value => setNextNumber(value)}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>

        <Typography.Text type="secondary">
          Example: set 491 so the next issued invoice is numbered …-0491.
        </Typography.Text>
      </Space>
    </Modal>
  );
};

export default NumberingModal;
